/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { BusinessType } from '../models/business-type';
import { CreatePaymentRequest } from '../models/create-payment-request';
import { GenericResponse } from '../models/generic-response';
import { GetPaymentResponse } from '../models/get-payment-response';
import { PaymentResponse } from '../models/payment-response';
import { TotalBalanceResponse } from '../models/total-balance-response';
import { UpdatePaymentRequest } from '../models/update-payment-request';

/**
 * payment API
 */
@Injectable({ providedIn: 'root' })
export class PaymentsService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllAttachments()` */
  static readonly GetAllAttachmentsPath = '/api/v1/payments';

  /**
   * Get list of payments.
   *
   * Gets the list of all the payments with any of the filters applied. <br>
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllAttachments()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllAttachments$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a filter for the payments:
       *   - InvoiceNumber
       */
      invoiceNumber?: number;

      /**
       * This adds a filter for the payments:
       *   - Advance payment ID
       */
      advancePaymentId?: number;

      /**
       * This allows filtering payments by their business type
       */
      businessType?: Array<BusinessType>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetPaymentResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PaymentsService.GetAllAttachmentsPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('invoiceNumber', params.invoiceNumber, {});
      rb.query('advancePaymentId', params.advancePaymentId, {});
      rb.query('businessType', params.businessType, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetPaymentResponse>;
        })
      );
  }

  /**
   * Get list of payments.
   *
   * Gets the list of all the payments with any of the filters applied. <br>
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllAttachments$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllAttachments(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a filter for the payments:
       *   - InvoiceNumber
       */
      invoiceNumber?: number;

      /**
       * This adds a filter for the payments:
       *   - Advance payment ID
       */
      advancePaymentId?: number;

      /**
       * This allows filtering payments by their business type
       */
      businessType?: Array<BusinessType>;
    },
    context?: HttpContext
  ): Observable<GetPaymentResponse> {
    return this.getAllAttachments$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<GetPaymentResponse>): GetPaymentResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `createPayment()` */
  static readonly CreatePaymentPath = '/api/v1/payments';

  /**
   * Create new payment.
   *
   * This API creates payments for invoices
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createPayment()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createPayment$Response(
    params: {
      body: CreatePaymentRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PaymentResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PaymentsService.CreatePaymentPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PaymentResponse>;
        })
      );
  }

  /**
   * Create new payment.
   *
   * This API creates payments for invoices
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createPayment$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createPayment(
    params: {
      body: CreatePaymentRequest;
    },
    context?: HttpContext
  ): Observable<PaymentResponse> {
    return this.createPayment$Response(params, context).pipe(
      map((r: StrictHttpResponse<PaymentResponse>): PaymentResponse => r.body)
    );
  }

  /** Path part for operation `getAttachmentById()` */
  static readonly GetAttachmentByIdPath = '/api/v1/payments/{id}';

  /**
   * fetch payment details.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAttachmentById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAttachmentById$Response(
    params: {
      /**
       * The payments id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PaymentResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PaymentsService.GetAttachmentByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PaymentResponse>;
        })
      );
  }

  /**
   * fetch payment details.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAttachmentById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAttachmentById(
    params: {
      /**
       * The payments id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<PaymentResponse> {
    return this.getAttachmentById$Response(params, context).pipe(
      map((r: StrictHttpResponse<PaymentResponse>): PaymentResponse => r.body)
    );
  }

  /** Path part for operation `deletePaymentById()` */
  static readonly DeletePaymentByIdPath = '/api/v1/payments/{id}';

  /**
   * Delete payment details by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deletePaymentById()` instead.
   *
   * This method doesn't expect any request body.
   */
  deletePaymentById$Response(
    params: {
      /**
       * The payment id that needs to be disabled
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PaymentsService.DeletePaymentByIdPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Delete payment details by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deletePaymentById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deletePaymentById(
    params: {
      /**
       * The payment id that needs to be disabled
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.deletePaymentById$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updatePaymentAttachmentById()` */
  static readonly UpdatePaymentAttachmentByIdPath = '/api/v1/payments/{id}';

  /**
   * Update payment by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updatePaymentAttachmentById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updatePaymentAttachmentById$Response(
    params: {
      /**
       * The payment id that needs to be updated
       */
      id: number;
      body: UpdatePaymentRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PaymentResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PaymentsService.UpdatePaymentAttachmentByIdPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PaymentResponse>;
        })
      );
  }

  /**
   * Update payment by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updatePaymentAttachmentById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updatePaymentAttachmentById(
    params: {
      /**
       * The payment id that needs to be updated
       */
      id: number;
      body: UpdatePaymentRequest;
    },
    context?: HttpContext
  ): Observable<PaymentResponse> {
    return this.updatePaymentAttachmentById$Response(params, context).pipe(
      map((r: StrictHttpResponse<PaymentResponse>): PaymentResponse => r.body)
    );
  }

  /** Path part for operation `getBalanceByInvoiceId()` */
  static readonly GetBalanceByInvoiceIdPath =
    '/api/v1/payments/total-balance/{id}';

  /**
   * fetch the total balance amount to be paid.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getBalanceByInvoiceId()` instead.
   *
   * This method doesn't expect any request body.
   */
  getBalanceByInvoiceId$Response(
    params: {
      /**
       * The invoice id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<TotalBalanceResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PaymentsService.GetBalanceByInvoiceIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<TotalBalanceResponse>;
        })
      );
  }

  /**
   * fetch the total balance amount to be paid.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getBalanceByInvoiceId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getBalanceByInvoiceId(
    params: {
      /**
       * The invoice id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<TotalBalanceResponse> {
    return this.getBalanceByInvoiceId$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<TotalBalanceResponse>): TotalBalanceResponse =>
          r.body
      )
    );
  }
}
